import { GridsterConfig, GridsterConfigService, type GridsterItem } from 'angular-gridster2';

import {
  ReportDashboardComponentGridPosition,
  ReportDashboardGridsterBaseConfig,
} from '@core/reports-types';

type GridItemInput = {
  $item: GridsterItem;
  notPlaced: boolean;
};

// functionality exported from gridster
function merge(obj1: any, obj2: any, properties: any): any {
  for (const p in obj2) {
    if (obj2[p] !== void 0 && properties.hasOwnProperty(p)) {
      if (typeof obj2[p] === 'object') {
        // create an empty object for the property if obj1 does not already have one.
        if (!(p in obj1)) {
          obj1[p] = {};
        }
        obj1[p] = merge(obj1[p], obj2[p], properties[p]);
      } else {
        obj1[p] = obj2[p];
      }
    }
  }

  return obj1;
}

export const getNextGridPosition = (
  grid: GridItemInput[],
  item: GridsterItem,
): ReportDashboardComponentGridPosition => {
  const gridHelper = new GridPositionHelper(grid);
  const response = gridHelper.getFirstPossiblePosition(item);
  return {
    x: response.x,
    y: response.y,
    cols: response.cols,
    rows: response.rows,
  };
};

export class GridPositionHelper {
  private readonly grid: GridItemInput[];
  private $options: Required<GridsterConfig>;
  private rows: number;
  private columns: number;

  constructor(grid: GridItemInput[], options: GridsterConfig = ReportDashboardGridsterBaseConfig) {
    const defaultOptions = JSON.parse(JSON.stringify(GridsterConfigService));
    this.$options = merge(defaultOptions, options, defaultOptions);

    this.columns = this.$options.minCols;
    this.rows = this.$options.minRows + this.$options.addEmptyRowsCount;
    this.grid = grid;
  }

  public getFirstPossiblePosition = (item: GridsterItem): GridsterItem => {
    const tmpItem = Object.assign({}, item);
    this.getNextPossiblePosition(tmpItem);
    return tmpItem;
  };

  public addItem(
    position: ReportDashboardComponentGridPosition,
  ): ReportDashboardComponentGridPosition {
    const itemComponent: GridItemInput = {
      $item: position,
      notPlaced: false,
    };
    if (itemComponent.$item.x === -1 || itemComponent.$item.y === -1) {
      this.autoPositionItem(itemComponent);
    } else if (this.checkCollision(itemComponent.$item)) {
      if (!this.$options.disableWarnings) {
        itemComponent.notPlaced = true;
        console.warn(
          "Can't be placed in the bounds of the dashboard, trying to auto position!/n" +
            JSON.stringify(itemComponent.$item, ['cols', 'rows', 'x', 'y']),
        );
      }
      if (!this.$options.disableAutoPositionOnConflict) {
        this.autoPositionItem(itemComponent);
      } else {
        itemComponent.notPlaced = true;
      }
    }
    this.grid.push(itemComponent);
    this.setGridDimensions();
    return itemComponent.$item;
  }

  public findItemWithItem(item: GridsterItem): GridItemInput | boolean {
    let widgetsIndex = 0;
    let widget: GridItemInput;
    for (; widgetsIndex < this.grid.length; widgetsIndex++) {
      widget = this.grid[widgetsIndex];
      if (widget.$item !== item && this.checkCollisionTwoItems(widget.$item, item)) {
        return widget;
      }
    }
    return false;
  }

  private autoPositionItem(itemComponent: GridItemInput): void {
    if (this.getNextPossiblePosition(itemComponent.$item)) {
      itemComponent.notPlaced = false;
    } else {
      itemComponent.notPlaced = true;
      if (!this.$options.disableWarnings) {
        console.warn(
          "Can't be placed in the bounds of the dashboard!/n" +
            JSON.stringify(itemComponent, ['cols', 'rows', 'x', 'y']),
        );
      }
    }
  }

  private getNextPossiblePosition = (
    newItem: GridsterItem,
    startingFrom: { y?: number; x?: number } = {},
  ): boolean => {
    if (newItem.cols === -1) {
      newItem.cols = this.$options.defaultItemCols;
    }
    if (newItem.rows === -1) {
      newItem.rows = this.$options.defaultItemRows;
    }
    this.setGridDimensions();
    let rowsIndex = startingFrom.y || 0;
    let colsIndex;
    for (; rowsIndex < this.rows; rowsIndex++) {
      newItem.y = rowsIndex;
      colsIndex = startingFrom.x || 0;
      for (; colsIndex < this.columns; colsIndex++) {
        newItem.x = colsIndex;
        if (!this.checkCollision(newItem)) {
          return true;
        }
      }
    }
    const canAddToRows = this.$options.maxRows >= this.rows + newItem.rows;
    const canAddToColumns = this.$options.maxCols >= this.columns + newItem.cols;
    const addToRows = this.rows <= this.columns && canAddToRows;
    if (!addToRows && canAddToColumns) {
      newItem.x = this.columns;
      newItem.y = 0;
      return true;
    } else if (canAddToRows) {
      newItem.y = this.rows;
      newItem.x = 0;
      return true;
    }
    return false;
  };

  private checkCollision(item: GridsterItem): GridItemInput | boolean {
    let collision: GridItemInput | boolean = false;
    if (!collision && this.checkGridCollision(item)) {
      collision = true;
    }
    if (!collision) {
      const c = this.findItemWithItem(item);
      if (c) {
        collision = c;
      }
    }
    return collision;
  }

  private checkCollisionTwoItems(item: GridsterItem, item2: GridsterItem): boolean {
    const collision =
      item.x < item2.x + item2.cols &&
      item.x + item.cols > item2.x &&
      item.y < item2.y + item2.rows &&
      item.y + item.rows > item2.y;
    if (!collision) {
      return false;
    }
    if (!this.$options.allowMultiLayer) {
      return true;
    }
    const defaultLayerIndex = this.$options.defaultLayerIndex;
    const layerIndex = item.layerIndex === undefined ? defaultLayerIndex : item.layerIndex;
    const layerIndex2 = item2.layerIndex === undefined ? defaultLayerIndex : item2.layerIndex;
    return layerIndex === layerIndex2;
  }

  private checkGridCollision(item: GridsterItem): boolean {
    const noNegativePosition = item.y > -1 && item.x > -1;
    const maxGridCols = item.cols + item.x <= this.$options.maxCols;
    const maxGridRows = item.rows + item.y <= this.$options.maxRows;
    const maxItemCols =
      item.maxItemCols === undefined ? this.$options.maxItemCols : item.maxItemCols;
    const minItemCols =
      item.minItemCols === undefined ? this.$options.minItemCols : item.minItemCols;
    const maxItemRows =
      item.maxItemRows === undefined ? this.$options.maxItemRows : item.maxItemRows;
    const minItemRows =
      item.minItemRows === undefined ? this.$options.minItemRows : item.minItemRows;
    const inColsLimits = item.cols <= maxItemCols && item.cols >= minItemCols;
    const inRowsLimits = item.rows <= maxItemRows && item.rows >= minItemRows;
    const minAreaLimit =
      item.minItemArea === undefined ? this.$options.minItemArea : item.minItemArea;
    const maxAreaLimit =
      item.maxItemArea === undefined ? this.$options.maxItemArea : item.maxItemArea;
    const area = item.cols * item.rows;
    const inMinArea = minAreaLimit <= area;
    const inMaxArea = maxAreaLimit >= area;
    return !(
      noNegativePosition &&
      maxGridCols &&
      maxGridRows &&
      inColsLimits &&
      inRowsLimits &&
      inMinArea &&
      inMaxArea
    );
  }

  private setGridDimensions(): void {
    let rows = this.$options.minRows;
    let columns = this.$options.minCols;

    let widgetsIndex = this.grid.length - 1;
    let widget;
    for (; widgetsIndex >= 0; widgetsIndex--) {
      widget = this.grid[widgetsIndex];
      if (!widget.notPlaced) {
        rows = Math.max(rows, widget.$item.y + widget.$item.rows);
        columns = Math.max(columns, widget.$item.x + widget.$item.cols);
      }
    }
    rows += this.$options.addEmptyRowsCount;
    if (this.columns !== columns || this.rows !== rows) {
      this.columns = columns;
      this.rows = rows;
    }
  }
}
